import groupBy from 'lodash/groupBy';
import moment from 'moment';
import { flexMinQuantityReached } from '../cart/actions/cart-actions';
import { ItemType } from '../classes/item-type';
import { PriceType } from '../classes/price-type';
import { IExchangeTicket } from '../classes/ticket-exchange';
import { loadAdditionalSeatingOptions, loadPriceRangeFilters, loadPriceTypeFilters, loadVenueLevelFilters } from '../filter/actions/filter-actions';
import { applyPromoCode, loadPromoCode } from '../filter/actions/promo-actions';
import isMobile from '../helpers/is-mobile';
import { clearStorageItemsWhere } from '../helpers/local-storage-helpers';
import { getParameterByName } from '../helpers/query-string-helpers';
import { IAppSettingsModel } from '../reducers/fetch-settings-reducer';
import { IAppState } from '../reducers/index';
import { calculateExchangePrice, loadExchanges, setActiveExchangeTickets } from './exchange-actions';
import { ActionType, ActionTypes } from './index';
import { showLoadingScreen, hideLoadingScreen } from './app-actions';
import { openSeatmapCart } from '../seatmap/actions/seatmap-actions';
import { dispatch } from 'd3';

export interface IFetchStateAction {
    type: ActionType;
    value?: any;
}

function receiveSettingsError(result) {
}

export function receiveSettings(result: IAppSettingsModel) {
    return (dispatch, state) => {

        dispatch({
            type: ActionTypes.RECEIVE_SETTINGS,
            value: result
        });
        const ticketsGrouped = groupBy(result.exchangeTickets, (ticket: IExchangeTicket) => ticket.oldPrice);
        let activeExchangeTickets = ticketsGrouped[Object.keys(ticketsGrouped)[0]];

        dispatch(setPriceSettings(result.priceTypeSettings, true, result.useOnlyPromoPriceType, activeExchangeTickets));

        let venueLevels = result.venueLevels.map(lvl => { lvl.selected = true; return lvl; });

        if (result.defaultSelectedLevelIds.length > 0) {
            venueLevels = venueLevels.map(lvl => { lvl.selected = result.defaultSelectedLevelIds.indexOf(lvl.levelNumber) >= 0; return lvl; })
        } else if (!result.defaultAllLevelsSelected) {
            venueLevels = venueLevels.map(lvl => { lvl.selected = false; return lvl; })
        }

        dispatch(loadVenueLevelFilters(venueLevels));

        const levelOverrides = (window as any).levelImageOverride;
        if (levelOverrides) {
            dispatch(setVenueLevelImageOverride(JSON.parse(levelOverrides)));
        }
        dispatch(loadAdditionalSeatingOptions(result.additionalSeatingOptions, result.renderAisleSeatToggle));

        if (result.currentPromoCode !== null && result.currentPromoCode.sourceCode > 0) {
            dispatch(loadPromoCode(result.currentPromoCode));
        } else if (result.currentPromoCode == null && (state() as IAppState).promoCodeModel.promoCodeId > 0) {
            dispatch(loadPromoCode(null));
        }

        if (result.itemType === ItemType.Exchange) {
            dispatch(loadExchanges(result.exchangeTickets));

            dispatch(setActiveExchangeTickets(activeExchangeTickets, []));
        } else if (result.itemType === ItemType.Flex || result.itemType === ItemType.Super) {
            dispatch(flexMinQuantityReached());
        }

        clearStorageItemsWhere(key => isOutdatedKey(key, moment(result.refreshSeatmapTimestamp)));
    }
}

export function setVenueLevelImageOverride(imageOverrides) {
    return (dispatch, state) => {

        dispatch({
            type: ActionTypes.SET_LEVEL_IMAGE_OVERRIDE,
            value: imageOverrides
        });
    };
}

export function setPriceSettings(priceTypeSettings: Array<PriceType>, initialLoad: boolean, useOnlyPromoPriceType: boolean, activeExchangeTickets: Array<IExchangeTicket>) {
    return (dispatch, state) => {

        let updatedPriceTypeSettings = priceTypeSettings;
        if (activeExchangeTickets !== undefined && activeExchangeTickets.length > 0) {
            updatedPriceTypeSettings = updatedPriceTypeSettings.map(ptSetting => {
                if (initialLoad) {
                    ptSetting.originalMaxPrice = ptSetting.maxPrice;
                    ptSetting.originalMinPrice = ptSetting.minPrice;
                    ptSetting.originalPrices = ptSetting.prices;
                }
                ptSetting.minPrice = calculateExchangePrice(ptSetting.originalMinPrice, activeExchangeTickets);
                ptSetting.minTotalPrice = calculateExchangePrice(ptSetting.minTotalPrice, activeExchangeTickets);
                ptSetting.maxPrice = calculateExchangePrice(ptSetting.originalMaxPrice, activeExchangeTickets);
                ptSetting.maxTotalPrice = calculateExchangePrice(ptSetting.maxTotalPrice, activeExchangeTickets);
                ptSetting.prices = ptSetting.originalPrices.map(pr => calculateExchangePrice(pr, activeExchangeTickets));
                return ptSetting;
            });
        }
        dispatch(loadPriceTypeFilters(updatedPriceTypeSettings, useOnlyPromoPriceType));
        dispatch(loadPriceRangeFilters(updatedPriceTypeSettings, useOnlyPromoPriceType));
    }
}

function isOutdatedKey(key: string, refreshSeatmapTimestamp: moment.Moment) {
    var timeStamp = getTimeStampFromKey(key);
    if (timeStamp != null) {
        return timeStamp.isBefore(refreshSeatmapTimestamp);
    } else {
        try {
            let localStorageState: IAppState = JSON.parse(localStorage.getItem(key));

            var performanceDate = getPerformanceDate(localStorageState);
            if (performanceDate != null) {
                return moment(performanceDate).isBefore(moment());
            }
        } catch (ex) {
            console.log("ERROR PARSING JSON:'" + key + "' from localStorage");
        }
    }
    return false;
}

function getPerformanceDate(appState: IAppState) {
    if (appState != undefined && appState != null
        && appState.appSettingsModel !== undefined && appState.appSettingsModel.performanceDate != null) {
        return appState.appSettingsModel.performanceDate
    }
    else {
        return null;
    }
}

function getTimeStampFromKey(key: string) {
    const timeStampIndex = key.indexOf('timestamp');
    if (timeStampIndex < 0) {
        return null;
    }
    return moment(key.substring(timeStampIndex + 10), 'yyyy-MM-dd h:mm a');
}


export function checkAndApplyPromo(newState: IAppState, dispatch, json, isSeatMap: boolean) {
    if (getParameterByName("promo") && getParameterByName("promo") !== "null"
        && newState.promoCodeModel.promoCodeId <= 0) {
        dispatch(applyPromoCode(getParameterByName("promo"), newState.appSettingsModel.currentCulture, true));
        dispatch(receiveSettings(json));

        if (isSeatMap) {
            dispatch(openSeatmapCart(false))
        }
    }
}

function requestSettings(itemId: number) {
    return {
        type: ActionTypes.REQUEST_SETTINGS,
        value: itemId
    }
}

function updateSettings(itemId: number, itemType, language) {
    return fetch(`${window.location.origin}/api/settings/GetAppSettings?itemId=${itemId}&itemType=${itemType}&language=${language}`, {
        credentials: 'same-origin'
    })
        .then(response => {
            return response.json();
        })
        .catch(error => {
            console.error(error);
        });

}

export function fetchSettings(itemId: number, itemType, language) {
    return (dispatch, state) => {
        dispatch(showLoadingScreen());
        updateSettings(itemId, itemType, language).then(json => {
            dispatch(receiveSettings(json));
            dispatch(hideLoadingScreen());
        });
    }
}
